import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Archive, ArchiveRestore, ArrowLeft, Edit, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useArchiveTestLabel, useDeleteTestLabel, useTestLabel } from "@/hooks/useTestLabels";
import { Button } from "@/components/ui/button";
import { MaskBadge } from "@/components/ui/mask-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DeleteActionDialog from "@/components/shared/DeleteActionDialog";
import { LoadingDisplay } from "../shared/LoadingDisplay";
import { GenericErrorDisplay } from "../shared/ErrorDisplay";
import { NotFoundDisplay } from "../shared/NotFoundDisplay";
import Tooltip from "../shared/Tooltip";
import { CreateMetaData } from "../shared/CreateMetaData";
import { useSpecList } from "@/hooks/useSpecLists";
import { Main } from "../shared/Main";
import { WarningAlert } from "../shared/Alerts";
import { Card, CardHeader, CardTitle } from "../ui/card";
import { useMaskStructures } from "@/hooks/useMasks";
import { Structure } from "@/types/structure";
import { Spec } from "@/types/specList";
import { usePermissionContext } from "@/context/PermissionContext";

const findMatchingStructure = (spec: Spec, structures: Structure[]) => {
  let match: Structure | null = null;

  for (const structure of structures) {
    const structureName = structure.name;
    if (
      structureName === spec.dutNames ||
      structureName === spec.reportName ||
      structureName === spec.legacyNames ||
      structureName === spec.name
    ) {
      match = structure;
      break;
    }
  }

  return match;
};

const TestLabelDetails = () => {
  const { testLabelId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissionContext();
  const { trigger: deleteTestLabel, isMutating: isExecutingDeletion } = useDeleteTestLabel();
  const { trigger: archiveTestLabel } = useArchiveTestLabel();

  const {
    data: testLabel,
    isLoading: isTestLabelsLoading,
    error: testLabelsError,
    mutate: refetchTestLabel,
  } = useTestLabel(testLabelId ? Number(testLabelId) : undefined);

  const usesStructures = (testLabel?.linkedStructures?.length || 0) > 0;

  const { data } = useMaskStructures(usesStructures ? undefined : testLabel?.mask?.id);

  const [isDeleteActionDialogOpen, setIsDeleteActionDialogOpen] = useState(false);

  const {
    data: specList,
    isLoading: isSpecListLoading,
    error: isSpecListError,
  } = useSpecList(testLabel?.specListId ?? null);

  const maskStructures = data?.structures ?? [];

  if (isTestLabelsLoading || isSpecListLoading) {
    return <LoadingDisplay />;
  }

  if (testLabelsError || isSpecListError) {
    return <GenericErrorDisplay />;
  }

  if (!testLabel) {
    return <NotFoundDisplay redirectUrl="/test-labels" text="Test Label not found" />;
  }

  const onEdit = () => navigate(`/test-labels/${testLabel.id}/edit`);

  const onDelete = async () => {
    await deleteTestLabel(
      { id: testLabel.id },
      {
        onError: (err) => toast.error(`Error: ${err.message}`),
        onSuccess: () => {
          toast.success(`Successfully deleted the Test Label "${testLabel.name}"!`);
          navigate("/test-labels");
        },
      },
    );
  };

  const handleArchiveToggle = async () => {
    try {
      const successMessage = testLabel.isArchived
        ? `Successfully unarchived Test Label ${testLabel.name}.`
        : `Successfully archived Test Label ${testLabel.name}!`;

      await archiveTestLabel(
        {
          id: testLabel.id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(successMessage);
            refetchTestLabel();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Main className="space-y-6">
      <Tooltip
        asChild
        content="Go back"
        trigger={
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft />
          </Button>
        }
        side="right"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">{testLabel.name}</h1>
            <div className="flex items-center gap-6 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                Mask: <MaskBadge maskName={testLabel.mask.name} />
              </span>
              {!usesStructures && (
                <span>
                  Spec List:{" "}
                  <Link
                    to={`/spec-lists/${specList?.id}/version/${specList?.latestVersion.id}`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {specList?.name}
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {testLabel.isArchived ? (
            <>
              <WarningAlert className={"flex justify-center h-9 items-center self-end"}>
                Test Label is archived and cannot be modified.
              </WarningAlert>
              <Button type="button" variant="secondary" onClick={handleArchiveToggle}>
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Unarchive
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={handleArchiveToggle}>
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
              {hasPermission("test_labels", "update") && testLabel.status === "NOT_VALIDATED" && (
                <Button type="button" onClick={onEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </>
          )}
          {hasPermission("test_labels", "delete") && testLabel.status === "NOT_VALIDATED" && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setIsDeleteActionDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="text-xs">
              Measurement Capability: {testLabel.measurementCapability.name}
            </span>
            <CreateMetaData createdAt={testLabel.createdAt} createdBy={testLabel.createdBy} />
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="flex justify-center w-full">
        <div className="w-[90%]">
          {usesStructures ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Structure Name</TableHead>
                  <TableHead>Device Class</TableHead>
                  <TableHead>Test Site Name</TableHead>
                  <TableHead>Nr. of Port Combos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testLabel.linkedStructures.map((structure) => (
                  <TableRow key={structure.id}>
                    <TableCell>{structure.name}</TableCell>
                    <TableCell>{structure.deviceClass}</TableCell>
                    <TableCell>{structure.testSiteName}</TableCell>
                    <TableCell>{structure.nrOfPortCombos}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Spec Name</TableHead>
                  <TableHead>Test Site Name</TableHead>
                  <TableHead>DUT Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testLabel.linkedSpecs.map((spec) => (
                  <TableRow key={spec.id}>
                    <TableCell>{spec.name}</TableCell>
                    <TableCell>
                      {findMatchingStructure(spec, maskStructures)?.testSiteName || "---"}
                    </TableCell>
                    <TableCell>{spec.dutNames}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <DeleteActionDialog
        action={onDelete}
        description={`Are you sure you want to delete Test Label: "${testLabel.name}"?`}
        isExecutingAction={isExecutingDeletion}
        open={isDeleteActionDialogOpen}
        onOpenChange={setIsDeleteActionDialogOpen}
        title="Delete Test Label"
      />
    </Main>
  );
};

export default TestLabelDetails;
